import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import { MAIN_MAP } from '../../../../../redux/map/Constants';
import { hideSecondaryPanel } from '../../../../../redux/panel/Actions';
import {
    setShapeFeatureProperty,
    savedDrawnFeaturesAsync,
    updateDrawnFeaturesAsync,
    fetchAllDrawingsAsync
} from '../../../../../redux/map/Actions';

import MoreVertIcon from '@material-ui/icons/MoreVert';
import {
    makeStyles,
    Typography,
    Grid,
    Menu,
    MenuItem,
    Popover,
    Button,
    InputAdornment
} from '@material-ui/core';

import SelectTextField from '../../../../Common/UI/SelectTextField';
import TextField from '../../../../Common/UI/TextField';
import SimpleHeading from '../../common/SimpleHeading';
import CreateSiteCardDialog from '../../../../Common/SiteCard/CreateDialog';
import ColourPicker from '../../../../Common/UI/ColourPicker';

const propTypes = {
    feature: PropTypes.object
};

const defaultProps = {};

const useStyles = makeStyles(theme => styles(theme));

function NewSketchesForm ({ feature }) {
    const classes = useStyles();
    const dispatch = useDispatch();
    const MAX = 1000;
    const MIN = 0;

    const [name, setName] = useState(feature?.properties?.name ? feature?.properties?.name : '');
    // Assigns default colour to shape if one has not been assigned already
    const [fillColour, setFillColour] = useState(
        feature?.properties?.fillColour ? feature?.properties?.fillColour : '#A9FE95'
    );
    const [strokeColour, setStrokeColour] = useState(
        feature?.properties?.strokeColour ? feature?.properties?.strokeColour : '#A9FE95'
    );
    const [strokeStyle, setStrokeStyle] = useState(
        feature?.properties?.strokeStyle ? feature?.properties?.strokeStyle : 'dashed'
    );
    const [saved, setSaved] = useState(
        feature?.properties?.saved !== undefined ? feature?.properties?.saved : true
    );
    const [error, setError] = useState(false);
    const [bufferError, setBufferError] = useState(false);
    const [bufferValue, setBufferValue] = useState(0);
    const [errorMessage, setErrorMessage] = useState('');
    const [menuOptionsEl, setMenuOptionsEl] = useState(null);
    const [fillMenuSketchEl, setFillMenuSketchEl] = useState(null);
    const [strokeMenuSketchEl, setStrokeMenuSketchEl] = useState(null);
    const [isCreateSiteCardDialogOpen, setIsCreateSiteCardDialogOpen] = useState(false);

    const drawModule = useSelector(state => state[MAIN_MAP].drawModule);
    const savedDrawnFeatures = useSelector(state => state[MAIN_MAP].savedDrawnFeatures);
    const isSavingDrawing = useSelector(state => state[MAIN_MAP].isSavingDrawing);
    const { drawControl } = drawModule;
    const { firstName } = useSelector(state => state.user);
    const savedResult = savedDrawnFeatures.find(saved => {
        saved?.properties?.id
            ? saved?.properties.id === feature?.properties?.id
            : saved?.id === feature?.id;
    });
    const projectId = useSelector(state => state.project.currentProject.id);
    const { saveDrawnFeatures } = useSelector(state => state.user.features.drawnFeatures);

    function dispatchHideSecondaryPanel () {
        return dispatch(hideSecondaryPanel());
    }
    function dispatchSetShapeFeatureProperty (userProperty, userPropertyValue) {
        return dispatch(
            setShapeFeatureProperty(feature?.id, userProperty, userPropertyValue, MAIN_MAP)
        );
    }
    function dispatchSetSavedDrawnFeaturesAsync (params) {
        return dispatch(savedDrawnFeaturesAsync(projectId, params));
    }
    function dispatchSetUpdateDrawnFeaturesAsync (params) {
        return dispatch(updateDrawnFeaturesAsync(feature?.properties?.id, params));
    }
    function dispatchFetchDrawnFeaturesAsync () {
        return dispatch(fetchAllDrawingsAsync(projectId));
    }

    function handleStrokeColourChange (colour) {
        setStrokeColour(colour.hex);
        setSaved(false);
        dispatchSetShapeFeatureProperty('saved', saved);
        if (feature?.id) {
            drawControl.setFeatureProperty(feature?.id, 'strokeColour', colour.hex);
            let result = drawControl.get(feature?.id);
            drawControl.add(result);
        }
    }

    function handleFillColourChange (colour) {
        setFillColour(colour.hex);
        setSaved(false);
        dispatchSetShapeFeatureProperty('saved', saved);
        if (feature?.id) {
            drawControl.setFeatureProperty(feature?.id, 'fillColour', colour.hex);
            let result = drawControl.get(feature?.id);
            drawControl.add(result);
        }
    }

    function handleStrokeStyle (e) {
        setStrokeStyle(e.target.value);
        setSaved(false);
        dispatchSetShapeFeatureProperty('saved', saved);
        if (feature?.id) {
            // Set default stroke colour if one hasn't been set just in case
            dispatchSetShapeFeatureProperty('strokeStyle', e.target.value);
            drawControl.setFeatureProperty(feature?.id, 'strokeColour', strokeColour);
            let result = drawControl.get(feature?.id);
            drawControl.add(result);
        }
    }

    // Save on submission too just in-case if the user wishes not to make any
    // changes to the shapes design and have the default value assigned instead
    function setDefaultPropertyOnShape (feature) {
        dispatchSetShapeFeatureProperty('fillColour', fillColour.toUpperCase());
        dispatchSetShapeFeatureProperty('strokeStyle', strokeStyle);
        dispatchSetShapeFeatureProperty('strokeColour', strokeColour.toUpperCase());
        dispatchSetShapeFeatureProperty('name', name.trim());
        dispatchSetShapeFeatureProperty('saved', true);

        let result = drawControl.get(feature?.id);
        drawControl.add(result);

        if (feature?.properties?.id) {
            dispatchSetUpdateDrawnFeaturesAsync(result);
            return;
        }

        dispatchSetSavedDrawnFeaturesAsync(result);
    }

    function bufferNumber (number) {
        const input = Number(number);
        const isValid = input >= MIN && input <= MAX;
        setBufferError(!isValid);
        if (isValid) {
            setBufferValue(input);
            setSaved(false);
        }
    }

    function handleSubmission (feature) {
        // Check if name field is empty
        if (!name) {
            setError(true);
            setErrorMessage('Name cannot be empty');
            return;
        }

        const isSavedFeature = feature.properties.id !== undefined;

        // If shape is previously saved, and the saved name is changed by user,
        // check whether the changed name exists already in the database
        if (isSavedFeature) {
            const isNameExists = savedDrawnFeatures.some(
                value =>
                    value.properties.name === name && value.properties.id !== feature.properties.id
            );
            if (isNameExists) {
                setError(true);
                setErrorMessage('Name exists already');
                return;
            }
        } else {
            // If a new shape is drawn, check if name exists in the database
            if (savedDrawnFeatures.some(value => value.properties.name === name)) {
                setError(true);
                setErrorMessage('Name exists already');
                return;
            }
        }

        if (bufferValue > 0) {
            dispatchSetShapeFeatureProperty('buffer', bufferValue);
        }

        setError(false);
        dispatchHideSecondaryPanel();
        setDefaultPropertyOnShape(feature);
    }

    const handleShapeNameChange = event => {
        setName(event.target.value);
        dispatchSetShapeFeatureProperty('name', event.target.value.trim());
        setSaved(false);
    };

    function findCoordinateChange (object1, object2) {
        if (Array.isArray(object1) && Array.isArray(object2)) {
            if (object1.length !== object2.length) {
                return [object1, object2];
            } else {
                for (let i = 0; i < object1.length; i++) {
                    if (object1[i] !== object2[i]) {
                        return [object1, object2];
                    }
                }
            }
        } else if (typeof object1 === 'object' && typeof object2 === 'object') {
            if (object1.coordinates && object2.coordinates) {
                return findCoordinateChange(object1.coordinates, object2.coordinates);
            }
        }
        return [];
    }

    useEffect(() => {
        setSaved(false);
        dispatchSetShapeFeatureProperty('saved', saved);
    }, [feature?.properties?.radius]);

    useEffect(() => {
        dispatchFetchDrawnFeaturesAsync();
    }, [projectId]);

    useEffect(() => {
        setFillColour(
            feature?.properties?.fillColour ? feature?.properties?.fillColour : '#A9FE95'
        );
        setStrokeColour(
            feature?.properties?.strokeColour ? feature?.properties?.strokeColour : '#A9FE95'
        );
        setStrokeStyle(
            feature?.properties?.strokeStyle ? feature?.properties?.strokeStyle : 'dashed'
        );
        setName(feature?.properties?.name ? feature?.properties?.name : '');
        setSaved(feature?.properties?.saved !== undefined ? feature?.properties?.saved : true);

        if (savedResult) {
            if (!feature?.properties?.radius) {
                if (findCoordinateChange(savedResult.geometry, feature.geometry)) setSaved(false);
            }
        }
    }, [feature?.id]);

    return (
        <>
            <CreateSiteCardDialog
                open={isCreateSiteCardDialogOpen}
                onClose={() => setIsCreateSiteCardDialogOpen(false)}
                defaultName={name ? name : `${firstName}'s shape`}
                featureConfig={{
                    layerId: 159,
                    isDrawn: true
                }}
                geometry={feature.geometry}
            />

            <div className={classes.border} />

            <SimpleHeading label="1. Define your shape name" />

            <Grid container spacing={1} alignItems="center">
                <Grid item xs={4}>
                    <Typography variant="body1" color="textPrimary">
                        Name
                    </Typography>
                </Grid>
                <Grid item xs={8}>
                    <TextField
                        error={Boolean(error)}
                        helperText={error ? errorMessage : null}
                        fullWidth
                        variant="outlined"
                        placeholder="Ensure the name is unique"
                        color="primary"
                        value={name}
                        onChange={e => handleShapeNameChange(e)}
                    />
                </Grid>
            </Grid>

            <div className={classes.border} />

            <SimpleHeading label="2. Edit your shape style" />

            {/* BUFFER FOR LINESTRING COMMENTED UNTIL FURTHER KNOWLEDGE ON IMPLEMENTATION */}

            {/* {Boolean(feature?.geometry?.type === 'LineString') && (
                <Grid container spacing={1} alignItems="center">
                    <Grid item xs={4}>
                        <InputLabel
                            label="Buffer"
                            tooltip="This will add a buffer around the drawn line. The max buffer size is 1000m. The buffer area will be included in the site card analysis."
                        />
                    </Grid>
                    <Grid item xs={8}>
                        <TextField
                            type="number"
                            error={bufferError}
                            helperText={bufferError ? 'Buffer must be between 0m and 1000m' : null}
                            fullWidth
                            value={bufferValue}
                            InputProps={{
                                inputProps: {
                                    max: MAX,
                                    min: MIN
                                },
                                startAdornment: <InputAdornment position="start">m</InputAdornment>
                            }}
                            onKeyDown={e => {
                                if (
                                    e.key === 'e' ||
                                    e.key === 'E' ||
                                    e.key === '-' ||
                                    e.key === '+'
                                ) {
                                    e.preventDefault();
                                }
                            }}
                            onChange={e => bufferNumber(e.target.value)}
                        />
                    </Grid>
                </Grid>
            )} */}

            {Boolean(feature?.geometry?.type === 'Polygon') && (
                <Grid container spacing={1} alignItems="center">
                    <Grid item xs={4}>
                        <Typography variant="body1" color="textPrimary">
                            Fill colour
                        </Typography>
                    </Grid>
                    <Grid item xs={8}>
                        <Button
                            variant="outlined"
                            size="large"
                            fullWidth
                            classes={{ root: classes.fillColourButton }}
                            disabled={feature?.properties?.isLocked ? true : false}
                            onClick={e => setFillMenuSketchEl(e.currentTarget)}>
                            <Typography variant="body1" color="textPrimary">
                                {fillColour}
                            </Typography>

                            <div
                                className={classes.fillColourCircle}
                                style={{ background: fillColour }}
                            />
                        </Button>
                        <Popover
                            open={Boolean(fillMenuSketchEl)}
                            anchorEl={fillMenuSketchEl}
                            onClose={() => setFillMenuSketchEl(null)}
                            anchorOrigin={{
                                vertical: 'top',
                                horizontal: 'center'
                            }}
                            transformOrigin={{
                                vertical: 'bottom',
                                horizontal: 'center'
                            }}>
                            <ColourPicker color={fillColour} onChange={handleFillColourChange} />
                        </Popover>
                    </Grid>
                </Grid>
            )}

            <Grid container spacing={1} alignItems="center">
                <Grid item xs={4}>
                    <Typography variant="body1" color="textPrimary">
                        {feature?.geometry?.type === 'Point' ? `Point` : `Stroke`} colour
                    </Typography>
                </Grid>
                <Grid item xs={8}>
                    <Button
                        variant="outlined"
                        size="large"
                        fullWidth
                        classes={{ root: classes.strokeColourButton }}
                        disabled={feature?.properties?.isLocked ? true : false}
                        onClick={e => setStrokeMenuSketchEl(e.currentTarget)}>
                        <Typography variant="body1" color="textPrimary">
                            {strokeColour}
                        </Typography>

                        <div
                            className={classes.strokeColourCircle}
                            style={{ background: strokeColour }}
                        />
                    </Button>
                    <Popover
                        open={Boolean(strokeMenuSketchEl)}
                        anchorEl={strokeMenuSketchEl}
                        onClose={() => setStrokeMenuSketchEl(null)}
                        anchorOrigin={{
                            vertical: 'top',
                            horizontal: 'center'
                        }}
                        transformOrigin={{
                            vertical: 'bottom',
                            horizontal: 'center'
                        }}>
                        <ColourPicker color={strokeColour} onChange={handleStrokeColourChange} />
                    </Popover>
                </Grid>
            </Grid>

            {Boolean(feature?.geometry?.type !== 'Point') && (
                <Grid container spacing={1} alignItems="center">
                    <Grid item xs={4}>
                        <Typography variant="body1" color="textPrimary">
                            Stroke style
                        </Typography>
                    </Grid>
                    <Grid item xs={8}>
                        <SelectTextField
                            name="strokestyle"
                            fullWidth
                            value={strokeStyle}
                            disabled={feature?.properties?.isLocked ? true : false}
                            onChange={e => handleStrokeStyle(e)}>
                            <MenuItem value="solid">Solid</MenuItem>
                            <MenuItem value="dashed">Dashed</MenuItem>
                        </SelectTextField>
                    </Grid>
                </Grid>
            )}
            <div className={classes.border} />

            {!saved && saveDrawnFeatures && (
                <div className={classes.warningBox}>
                    <Typography variant="body1" color="textPrimary">
                        Unsaved changes
                    </Typography>
                    <Typography variant="body2" color="textPrimary">
                        Please save the shape before exiting this panel. The shape will revert when
                        the LandHawk refreshes if it's not saved.
                    </Typography>
                </div>
            )}

            {saveDrawnFeatures && (
                <div className={classes.wrapper}>
                    <Button
                        classes={{ root: classes.removeButton }}
                        variant="outlined"
                        size="large"
                        onClick={() => dispatchHideSecondaryPanel()}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        size="large"
                        color="secondary"
                        disabled={saved || isSavingDrawing}
                        classes={{ root: classes.saveButton }}
                        onClick={() => handleSubmission(feature)}>
                        {feature?.properties?.id ? `Update` : `Save `} sketch
                    </Button>

                    {(Boolean(feature?.geometry?.type === 'Polygon') ||
                        Boolean(feature?.geometry?.type === 'LineString')) && (
                            <>
                                <Button
                                    variant="contained"
                                    size="small"
                                    color="secondary"
                                    classes={{ root: classes.moreButton }}
                                    onClick={e => setMenuOptionsEl(e.currentTarget)}>
                                    <MoreVertIcon />
                                </Button>
                                <Menu
                                    open={Boolean(menuOptionsEl)}
                                    anchorEl={menuOptionsEl}
                                    onClose={() => setMenuOptionsEl(null)}>
                                    <MenuItem
                                        onClick={() => {
                                            setIsCreateSiteCardDialogOpen(true);
                                            setMenuOptionsEl(null);
                                        }}>
                                        Save as site card
                                    </MenuItem>
                                </Menu>
                            </>
                        )}
                </div>
            )}
        </>
    );
}

const styles = theme => ({
    border: {
        height: '2px',
        backgroundColor: theme.palette.background.border
    },
    wrapper: {
        display: 'flex',
        height: '40px',
        gap: '10px',
        paddingTop: '10px'
    },
    removeButton: {
        width: '165px',
        color: theme.palette.grey.variant4,
        border: `1px solid ${theme.palette.grey.variant4}`
    },
    saveButton: {
        flexGrow: 1
    },
    moreButton: {
        minWidth: '40px'
    },
    strokeColourCircle: {
        width: '25px',
        height: '25px',
        borderRadius: '50%',
        marginLeft: '5px'
    },
    fillColourButton: {
        height: '40px',
        borderColor: `${theme.palette.grey.variant3}`
    },
    strokeColourButton: {
        height: '40px',
        borderColor: `${theme.palette.grey.variant3}`
    },
    strokeStyleDrop: {
        height: '20px'
    },
    fillColourCircle: {
        width: '25px',
        height: '25px',
        borderRadius: '50%',
        marginLeft: '5px'
    },
    warningBox: {
        display: 'flex',
        flexDirection: 'column',
        minHeight: 'auto',
        backgroundColor: '#FFE5A033',
        padding: '10px',
        border: '1px solid #FFE5A0'
    }
});

NewSketchesForm.propTypes = propTypes;
NewSketchesForm.defaultProps = defaultProps;

export default NewSketchesForm;
